import api from '@/libs/axiosInstance';
import { planFeaturesQueryKey } from '@/hooks/queries/use-plan-features-query';
import { useCurrentBrandQuery } from '@/hooks/queries/use-current-brand-query';
import { briefTemplateKeys, BRIEF_TEMPLATES_PATH } from '@/hooks/queries/brief-template-keys';
import type {
  ApiResponse,
  BriefTemplateResponse,
  CreateBriefTemplateRequest,
  DuplicateBriefTemplateRequest,
  UpdateBriefTemplateRequest,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface WithTemplateId {
  id: string;
}

/**
 * Library-management mutations for collab brief templates: create, rename,
 * duplicate, archive, restore, delete, and freeze-version. Every mutation goes
 * through one small factory (request + list invalidation) so the shape stays
 * uniform. The editor autosave lives in its own hook because it patches the
 * detail cache silently instead of invalidating.
 */
export const useBriefTemplateActions = () => {
  const queryClient = useQueryClient();
  const { data: brand } = useCurrentBrandQuery();
  const brandId = brand?.id ?? '';

  const invalidateLists = () => {
    void queryClient.invalidateQueries({ queryKey: planFeaturesQueryKey });
    void queryClient.invalidateQueries({ queryKey: briefTemplateKeys.all(brandId) });
  };

  /**
   * Shared mutation skeleton: run the request, then refresh the lists. Local
   * hook factory — always called the same number of times per render, so the
   * rules of hooks hold.
   */
  const useTemplateMutation = <TVariables, TData = BriefTemplateResponse>(
    request: (variables: TVariables) => Promise<TData>
  ) =>
    useMutation<TData, Error, TVariables>({
      mutationFn: request,
      onSuccess: invalidateLists,
    });

  const unwrap = <T>(data: ApiResponse<T>): T => data.data as T;

  const create = useTemplateMutation<CreateBriefTemplateRequest>(async (body) => {
    const { data } = await api.post<ApiResponse<BriefTemplateResponse>>(BRIEF_TEMPLATES_PATH, body);
    return unwrap(data);
  });

  /** Library-side PATCH by id (rename, or persist a wizard-authored content). */
  const update = useTemplateMutation<WithTemplateId & UpdateBriefTemplateRequest>(
    async ({ id, ...body }) => {
      const { data } = await api.patch<ApiResponse<BriefTemplateResponse>>(
        `${BRIEF_TEMPLATES_PATH}/${id}`,
        body
      );
      return unwrap(data);
    }
  );

  const duplicate = useTemplateMutation<WithTemplateId & DuplicateBriefTemplateRequest>(
    async ({ id, name }) => {
      const { data } = await api.post<ApiResponse<BriefTemplateResponse>>(
        `${BRIEF_TEMPLATES_PATH}/${id}/duplicate`,
        name ? { name } : {}
      );
      return unwrap(data);
    }
  );

  const archive = useTemplateMutation<WithTemplateId>(async ({ id }) => {
    const { data } = await api.post<ApiResponse<BriefTemplateResponse>>(
      `${BRIEF_TEMPLATES_PATH}/${id}/archive`
    );
    return unwrap(data);
  });

  const restore = useTemplateMutation<WithTemplateId>(async ({ id }) => {
    const { data } = await api.post<ApiResponse<BriefTemplateResponse>>(
      `${BRIEF_TEMPLATES_PATH}/${id}/restore`
    );
    return unwrap(data);
  });

  const remove = useTemplateMutation<WithTemplateId, void>(async ({ id }) => {
    await api.delete(`${BRIEF_TEMPLATES_PATH}/${id}`);
  });

  /** Freeze the current draft into an immutable version (called on editor exit). */
  const freezeVersion = useMutation<void, Error, WithTemplateId>({
    mutationFn: async ({ id }) => {
      await api.post(`${BRIEF_TEMPLATES_PATH}/${id}/versions`);
    },
  });

  return { create, update, duplicate, archive, restore, remove, freezeVersion };
};
